import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Search, Truck, Flag, FlagOff, Camera } from "lucide-react";
import AuthImage from "@/components/app/AuthImage";
import { toast } from "sonner";

export default function VehicleHistory() {
  const [filters, setFilters] = useState({
    date_from: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10),
    date_to: new Date().toISOString().slice(0, 10),
    vehicle_id: "all",
    phase: "post",
    q: "",
  });
  const [vehicles, setVehicles] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, []);

  const search = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (params.vehicle_id === "all") delete params.vehicle_id;
      if (params.phase === "all") delete params.phase;
      if (!params.q) delete params.q;
      const { data } = await api.get("/vehicles/history", { params });
      setRows(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((r) => {
      const d = r.scheduled_date || "—";
      g[d] = g[d] || [];
      g[d].push(r);
    });
    return Object.entries(g).sort(([a], [b]) => b.localeCompare(a));
  }, [rows]);

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Archivio</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Storico Mezzi — Ritorni</h1>
        <p className="text-slate-500 mt-2 text-sm">Vedi i rientri dei furgoni (km finali, € diesel, foto) per data, mezzo, caposquadra o cliente. Puoi anche filtrare per Partenza o entrambe.</p>
      </header>

      <section className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="history-filters">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="text-xs">Dal</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                   data-testid="history-date-from-input" />
          </div>
          <div>
            <Label className="text-xs">Al</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                   data-testid="history-date-to-input" />
          </div>
          <div>
            <Label className="text-xs">Mezzo</Label>
            <Select value={filters.vehicle_id} onValueChange={(v) => setFilters({ ...filters, vehicle_id: v })}>
              <SelectTrigger data-testid="history-vehicle-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Fase</Label>
            <Select value={filters.phase} onValueChange={(v) => setFilters({ ...filters, phase: v })}>
              <SelectTrigger data-testid="history-phase-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="post">Ritorno (default)</SelectItem>
                <SelectItem value="pre">Partenza</SelectItem>
                <SelectItem value="all">Tutte</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-1">
            <Label className="text-xs">Cerca testo</Label>
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                   placeholder="caposquadra, cliente, targa…" data-testid="history-search-input" />
          </div>
          <div>
            <Button onClick={search} disabled={loading} className="w-full h-10 bg-sky-600 hover:bg-sky-700 text-white"
                    data-testid="history-search-button">
              <Search className="w-4 h-4 mr-1" /> Cerca
            </Button>
          </div>
        </div>
      </section>

      <section className="space-y-6" data-testid="history-results">
        {rows.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-400">
            Nessuna ispezione trovata nei filtri selezionati.
          </div>
        ) : (
          grouped.map(([date, items]) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-3">
                <div className="font-mono text-xs uppercase tracking-widest font-bold text-slate-400">{date}</div>
                <div className="h-px flex-1 bg-slate-200" />
                <div className="text-xs text-slate-500 font-semibold">{items.length} ispezioni</div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {items.map((r) => (
                  <div key={r.id} className={`bg-white rounded-xl border p-4 ${r.phase === "pre" ? "border-sky-200" : "border-emerald-200"}`}
                       data-testid={`history-row-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {r.phase === "pre" ? <Flag className="w-4 h-4 text-sky-700" /> : <FlagOff className="w-4 h-4 text-emerald-700" />}
                        <span className={`font-display font-bold uppercase text-[11px] tracking-widest ${r.phase === "pre" ? "text-sky-800" : "text-emerald-800"}`}>
                          {r.phase === "pre" ? "Pre-Partenza" : "Post-Ritorno"}
                        </span>
                      </div>
                      <span className="text-[10px] font-mono text-slate-400">
                        {r.timestamp ? new Date(r.timestamp).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : ""}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <Truck className="w-4 h-4 text-slate-500" />
                      <span className="font-mono font-bold tracking-wider text-slate-900">{r.vehicle_plate || "—"}</span>
                      <span className="text-slate-500">{r.vehicle_name}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Caposquadra</div>
                        <div className="font-semibold text-slate-900">{r.foreman_name}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Km</div>
                        <div className="font-mono font-bold text-slate-900">{r.km?.toLocaleString?.("it-IT")}</div>
                      </div>
                    </div>
                    <Link to={`/lavori/${r.job_id}`} className="block mt-2 text-xs text-sky-600 hover:text-sky-700 font-semibold">
                      → Cliente: {r.client_name}
                    </Link>
                    {r.note && <div className="text-xs italic text-slate-500 mt-2">{r.note}</div>}
                    {(r.photos || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {r.photos.slice(0, 4).map((p) => (
                          <button key={p.id}
                             type="button"
                             onClick={async () => {
                               try {
                                 const resp = await api.get(`/jobs/${r.job_id}/inspections/${r.id}/photos/${p.id}/download`, { responseType: "blob" });
                                 const u = URL.createObjectURL(resp.data);
                                 window.open(u, "_blank");
                                 setTimeout(() => URL.revokeObjectURL(u), 60000);
                               } catch (err) { toast.error(formatApiError(err)); }
                             }}
                             className="block">
                            <AuthImage path={`/jobs/${r.job_id}/inspections/${r.id}/photos/${p.id}/download`}
                                       className="w-16 h-16 rounded-lg object-cover border border-slate-200 cursor-zoom-in" />
                          </button>
                        ))}
                        {r.photos.length > 4 && (
                          <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">
                            +{r.photos.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
